import type { LevelEntry } from "./types";
import {
  pulse_storm,
  final_boss,
  sweep_gauntlet,
  mirror_maze,
  apex_gate,
} from "./bosses";

// Hand-crafted levels — composed like music; gap positions are the rhythm.
// Speeds in m/s. Tunnel radius is ~4m. Each segment lasts length/speed s.
//
// Sizing rule of thumb: keep an average level run ~40-60s of real time so
// the player has space to breathe between gates. Segments also scale ~1.4×
// longer than a tutorial pass to give the eye time to read the gap layout.
//
// Pickups:
//   - score  -> safe (non-gap) panel, generous z so the player has time
//   - shield -> usually a gap panel, mid-segment so the rotate-into-gap-
//               and-back maneuver is doable
//   - slowmo -> in a gap on a fast / dense-gap segment so it pays for the
//               risk by giving the player breathing room

// Level 1 — Warm-up. Gentle introduction to all three shapes + the
// pickup mechanic with a single safe-lane score orb so new players see
// what an orb looks like before any risk-pickup shows up.
export const level1: LevelEntry[] = [
  { shape: "octagon", speed: 10, length: 70, gaps: [3, 7] },
  {
    shape: "octagon",
    speed: 11,
    length: 64,
    gaps: [2, 6],
    pickups: [{ type: "score", panel: 0, z: 0.55 }],
  },
  { shape: "octagon", speed: 12, length: 56, gaps: [4] },
  { shape: "octagon", speed: 13, length: 52, gaps: [1, 5] },
  { shape: "gate", to: "square" },
  { shape: "square", speed: 14, length: 50, gaps: [1] },
  {
    shape: "square",
    speed: 15,
    length: 46,
    gaps: [3],
    pickups: [{ type: "score", panel: 0, z: 0.5 }],
  },
  { shape: "square", speed: 15, length: 42, gaps: [0, 2] },
  { shape: "square", speed: 16, length: 38, gaps: [2] },
  { shape: "gate", to: "triangle" },
  { shape: "triangle", speed: 16, length: 58, gaps: [0] },
  { shape: "triangle", speed: 17, length: 50, gaps: [1] },
  { shape: "triangle", speed: 18, length: 44, gaps: [2] },
  { shape: "triangle", speed: 18, length: 40, gaps: [0] },
  { shape: "gate", to: "octagon" },
  { shape: "octagon", speed: 19, length: 70, gaps: [1, 5] },
];

// Level 2 — Drift. Faster baseline; double and triple gaps demand
// continuous side-stepping in the octagon midsection. First shield orb
// and a slow-mo on the brutal triple-gap.
export const level2: LevelEntry[] = [
  { shape: "square", speed: 16, length: 44, gaps: [2] },
  { shape: "square", speed: 17, length: 40, gaps: [0] },
  { shape: "square", speed: 17, length: 36, gaps: [1] },
  { shape: "gate", to: "octagon" },
  { shape: "octagon", speed: 18, length: 56, gaps: [1, 5] },
  {
    shape: "octagon",
    speed: 19,
    length: 50,
    gaps: [3, 6],
    pickups: [{ type: "shield", panel: 3, z: 0.5 }],
  },
  {
    shape: "octagon",
    speed: 20,
    length: 46,
    gaps: [2, 4, 7],
    pickups: [{ type: "slowmo", panel: 2, z: 0.45 }],
  },
  // Mid-game sweep introduction. Sticking it on the second triple-gap
  // octagon (after the slow-mo and shield pickups) keeps the
  // difficulty curve smooth -- the player has tools.
  { shape: "octagon", speed: 20, length: 42, gaps: [0, 3, 6], trick: "sweep" },
  // Hexagon debut -- tucked between the sweep octagon and the
  // triangle gate so the player gets a fresh polygon count without
  // having to also handle a trick on the same segment.
  { shape: "gate", to: "hexagon" },
  { shape: "hexagon", speed: 20, length: 46, gaps: [3] },
  {
    shape: "hexagon",
    speed: 21,
    length: 42,
    gaps: [1, 4],
    pickups: [{ type: "score", panel: 0, z: 0.55 }],
  },
  { shape: "gate", to: "triangle" },
  { shape: "triangle", speed: 21, length: 44, gaps: [1] },
  // Mirror flips player controls for one short triangle so they get
  // exactly one "wait what?" moment before the next gate restores
  // normal rotation.
  { shape: "triangle", speed: 22, length: 40, gaps: [2], trick: "mirror" },
  { shape: "triangle", speed: 22, length: 38, gaps: [0] },
];

// Level 3 — Pulse. Shape morphs between every segment so the player has
// to re-learn their orientation constantly. Speeds stay moderate; the
// challenge is rhythm not raw velocity. Two safe-lane score orbs reward
// the disciplined player with low-risk bonus points.
export const level3: LevelEntry[] = [
  { shape: "octagon", speed: 18, length: 56, gaps: [3] },
  { shape: "gate", to: "triangle" },
  {
    shape: "triangle",
    speed: 19,
    length: 44,
    gaps: [0],
    pickups: [{ type: "score", panel: 1, z: 0.55 }],
  },
  { shape: "gate", to: "square" },
  { shape: "square", speed: 20, length: 40, gaps: [2] },
  { shape: "gate", to: "octagon" },
  {
    shape: "octagon",
    speed: 21,
    length: 48,
    gaps: [1, 5],
    pickups: [{ type: "shield", panel: 1, z: 0.55 }],
  },
  { shape: "gate", to: "triangle" },
  { shape: "triangle", speed: 22, length: 40, gaps: [2] },
  { shape: "gate", to: "square" },
  // First pulse-trick segment in story mode -- by level 3 the
  // player has the rotational vocabulary to handle a black-out wall.
  { shape: "square", speed: 23, length: 36, gaps: [1, 3], trick: "pulse" },
  // Pentagon debut. 5 walls + a single gap teaches the player to
  // commit faster because there are fewer "safe" panels to drift
  // toward.
  { shape: "gate", to: "pentagon" },
  { shape: "pentagon", speed: 23, length: 42, gaps: [2] },
  {
    shape: "pentagon",
    speed: 24,
    length: 38,
    gaps: [1, 3],
    pickups: [{ type: "shield", panel: 1, z: 0.5 }],
  },
  { shape: "gate", to: "triangle" },
  { shape: "triangle", speed: 23, length: 36, gaps: [0] },
  // Story-mode boss capper. Pulse storm tests the player's gap
  // memory before the gate to level 4.
  ...pulse_storm.entries,
];

// Level 4 — Storm. Top-tier speeds, tight gaps on every shape,
// triangle-only finale with sequential single-side gaps. A slow-mo on
// the first triangle of the finale hands the prepared player the keys
// to the kingdom; an additional shield orb just before it gives a
// safety net into the gauntlet.
export const level4: LevelEntry[] = [
  { shape: "octagon", speed: 22, length: 50, gaps: [2, 6] },
  {
    shape: "octagon",
    speed: 23,
    length: 44,
    gaps: [1, 4, 7],
    pickups: [{ type: "shield", panel: 1, z: 0.5 }],
  },
  { shape: "gate", to: "square" },
  { shape: "square", speed: 24, length: 36, gaps: [0, 2] },
  { shape: "square", speed: 25, length: 32, gaps: [1, 3] },
  { shape: "square", speed: 25, length: 28, gaps: [0, 3] },
  { shape: "gate", to: "triangle" },
  {
    shape: "triangle",
    speed: 26,
    length: 34,
    gaps: [0],
    pickups: [{ type: "slowmo", panel: 0, z: 0.4 }],
  },
  { shape: "triangle", speed: 27, length: 30, gaps: [1] },
  { shape: "triangle", speed: 28, length: 28, gaps: [2] },
  { shape: "triangle", speed: 28, length: 26, gaps: [0] },
  // Final boss caps the campaign. Combines all three tricks.
  ...final_boss.entries,
];

// Level 5 — Hex Mastery. The hexagon is the spotlight shape: 6 walls
// is "in between" enough that the player has to commit to two-step
// rotations, and the bank corners introduce sustained camera roll
// for the first time in the campaign. Mid-level we splice a
// sweep_gauntlet for a brutal trick run, capped by mirror_maze so
// the closing gauntlet rewires the player's controls one last time.
//
// Pacing: ~55s of real-time across 14 entries (gates included).
// Speeds top out at 25 m/s on the bank corners so the player can
// still read the gap layout while the camera is rolled ~25°.
export const level5: LevelEntry[] = [
  { shape: "octagon", speed: 20, length: 48, gaps: [3, 6] },
  { shape: "gate", to: "hexagon" },
  // First bank: a sustained right-leaning corner. BankingFx reads
  // the bank field on getSegmentAtPlayer and tilts the camera, so
  // the player gets a dramatic but readable cue that the world
  // itself is leaning.
  {
    shape: "hexagon",
    speed: 21,
    length: 56,
    gaps: [2],
    bank: 1,
    pickups: [{ type: "score", panel: 4, z: 0.55 }],
  },
  {
    shape: "hexagon",
    speed: 22,
    length: 50,
    gaps: [1, 4],
    pickups: [{ type: "shield", panel: 1, z: 0.5 }],
  },
  // Counter-bank corner. The roll snaps the other way; the gap
  // layout encourages the player to live on the high side.
  {
    shape: "hexagon",
    speed: 23,
    length: 48,
    gaps: [3, 5],
    bank: -1,
  },
  {
    shape: "hexagon",
    speed: 24,
    length: 44,
    gaps: [0, 3],
    pickups: [{ type: "score", panel: 1, z: 0.5 }],
  },
  // Mid-level sweep gauntlet. Player has tools (shield from earlier)
  // and the gauntlet's progression of oct -> sq -> tri rotates the
  // shape repertoire one more time before we pivot back to hexagon.
  // sweep_gauntlet ends with `gate -> octagon`, so we add one safe
  // octagon breather then re-gate to hexagon for the closing arc.
  ...sweep_gauntlet.entries,
  {
    shape: "octagon",
    speed: 22,
    length: 44,
    gaps: [3],
    pickups: [{ type: "double", panel: 0, z: 0.55 }],
  },
  { shape: "gate", to: "hexagon" },
  {
    shape: "hexagon",
    speed: 24,
    length: 46,
    gaps: [2, 5],
    pickups: [{ type: "magnet", panel: 0, z: 0.5 }],
  },
  // Closing mirror_maze: scrambled controls under hexagons. Already
  // bossEnd-terminated by the phrase, so the level's terminal gate
  // is contributed by mirror_maze itself.
  ...mirror_maze.entries,
];

// Level 6 — Final Run. The campaign capper. Every shape (pentagon
// to start, hexagon to anchor, octagon to finish), every trick
// (mirror, pulse, sweep) sprinkled across the runway, two bank
// corners for visual drama, and the brand-new apex_gate boss to
// close out. The bossEnd of apex_gate's mirror finale terminates
// the level so MorphHexGame.onSuccess fires once the bar exits.
//
// Pacing: ~70s of real-time across 18 entries.
export const level6: LevelEntry[] = [
  { shape: "pentagon", speed: 22, length: 46, gaps: [2] },
  {
    shape: "pentagon",
    speed: 23,
    length: 42,
    gaps: [1, 3],
    trick: "mirror",
    pickups: [{ type: "shield", panel: 0, z: 0.5 }],
  },
  { shape: "gate", to: "hexagon" },
  // First bank in the final level -- mirror trick + bank stacks the
  // visual disorientation. Mirror's input invert plus the camera
  // roll teaches the player not to trust either reference frame.
  {
    shape: "hexagon",
    speed: 23,
    length: 50,
    gaps: [3],
    bank: 1,
    trick: "mirror",
  },
  {
    shape: "hexagon",
    speed: 24,
    length: 44,
    gaps: [1, 4],
    pickups: [{ type: "magnet", panel: 0, z: 0.5 }],
  },
  {
    shape: "hexagon",
    speed: 24,
    length: 44,
    gaps: [2, 5],
    trick: "pulse",
  },
  // Second bank, opposite direction, plus a double-score so the
  // skilled player gets a fat reward for staying composed.
  {
    shape: "hexagon",
    speed: 25,
    length: 42,
    gaps: [0, 3],
    bank: -1,
    pickups: [{ type: "double", panel: 4, z: 0.5 }],
  },
  { shape: "gate", to: "octagon" },
  // Final octagon stretch before the boss. Sweep + tight gaps =
  // last skill check before the gravity flip.
  {
    shape: "octagon",
    speed: 26,
    length: 44,
    gaps: [1, 5],
    trick: "sweep",
    pickups: [{ type: "shield", panel: 0, z: 0.45 }],
  },
  {
    shape: "octagon",
    speed: 26,
    length: 40,
    gaps: [2, 6],
    pickups: [{ type: "score", panel: 0, z: 0.5 }],
  },
  { shape: "gate", to: "hexagon" },
  // Apex Gate finale. Combines reverse_gate's gravity flip with
  // sweep + mirror layered on the closing two segments.
  ...apex_gate.entries,
];

export const LEVELS: LevelEntry[][] = [
  level1,
  level2,
  level3,
  level4,
  level5,
  level6,
];

// Display names for the level-select screen. Index N corresponds to
// LEVELS[N], so this stays in lockstep with the LEVELS array.
// Anything that ships a new level MUST also add a name entry here so
// the LevelSelectScreen card has something to render.
export const STORY_LEVEL_NAMES: string[] = [
  "Warm-Up",
  "Drift",
  "Pulse",
  "Storm",
  "Hex Mastery",
  "Final Run",
];
